import React, { useContext, useEffect } from 'react';
import { CButton, CForm, CModal, CModalBody, CModalFooter, CModalHeader } from '@coreui/react';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { connect, DispatchProp, useSelector } from 'react-redux';
import { isValidPhoneNumber } from 'react-phone-number-input/input';
import TechnicianForm, { TechnicianFormMode } from '../../components/TechnicianForm';
import LoadingButton from '../../../shared/components/LoadingButton';
import { ResetUpdateUserState, UpdateUser } from '../../actions/userManagement.action';
import { UserInfo, UserPayload, UserRoles } from '../../../shared/models/user';
import { RootState } from '../../../store/state';
import { selectUserProfile } from '../../../store/selectors/profile.selectors';
import { ConfigContext } from '../../../shared/contexts/journeyConfig.context';
import phoneUnification from '../../../shared/utils/validation-utils';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  phone: Yup.string()
  .nullable()
  .test(
    'phone-validation',
    'Please enter a valid US or Canada number',
    (value) => {
      if (!value) return true;
      const phone = phoneUnification(value);
      return phone && isValidPhoneNumber(phone);
    }
  ),
  roles: Yup.array().min(1, 'At least one role is required'),
  email: Yup.string().email('Email must be valid').required('Email is required'),
  password: Yup.string().matches(/^(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/, 'Must contain 8 characters, at least 1 uppercase, 1 number'),
  // eslint-disable-next-line func-names
  passwordConfirm: Yup.string().test('passwords-match', 'Passwords must match', function(value) {
    return this.parent.password === value;
  }),
  organization: Yup.number().required('Org id is required'),
});

export interface EditTechnicianProps extends DispatchProp {
  technician: UserInfo;
  show: boolean;
  close: () => void;
  saving: boolean;
  saved: boolean;
}

const EditTechnicianModal = (props: EditTechnicianProps) => {
  const { technician, show, close, saving, saved, dispatch } = props;
  const labels = useContext(ConfigContext);

  const profile = useSelector(selectUserProfile);

  const onSubmit = (values: UserPayload) => {
    dispatch(new UpdateUser({ userId: technician.id, body: values }));
  };

  const formik = useFormik({
      initialValues: {
        name: technician.name,
        phone: phoneUnification(technician.info.phone),
        email: technician.email,
        roles: technician.roles,
        avatar: technician.avatar,
        username: technician.username,
        organization: technician.orgId,
      } as UserPayload,
      validationSchema,
      onSubmit,
    },
  );

  const handleClose = () => {
    close();
    formik.resetForm();
  };

  const isEditable = () => {
    if (profile.id === technician.id || profile.roles.indexOf(UserRoles.Admin) > - 1) {
      return true;
    }
    if (profile.roles.indexOf(UserRoles.Manager) > -1) {
      return !(technician.roles.indexOf(UserRoles.Admin) > -1 || technician.roles.indexOf(UserRoles.Manager) > -1);

    }
    return false;
  };

  useEffect(() => {
    if (saved) {
      dispatch(new ResetUpdateUserState());
      handleClose();
    }
  }, [saved]);

  return (
    show && <CModal className="mt-5 pt-5" id="edit-technician-modal" show={ show } onClose={ handleClose } closeOnBackdrop={ false }>
      <CModalHeader closeButton>Edit {labels.agent}</CModalHeader>
      <CForm onSubmit={ formik.handleSubmit }>
        <FormikProvider value={formik}>
          <CModalBody>
            <TechnicianForm formik={ formik } disabled={!isEditable()} mode={TechnicianFormMode.Edit} />
          </CModalBody>
          <CModalFooter className="glympse-footer">
          <div className="glympse-btnWrapper">
            <LoadingButton color='primary' className='bg-color-orange border-0 roundedBtn roundBottomShadow pl-5 pr-5' type='submit' isLoading={ saving }>Save {labels.agent}</LoadingButton>
          </div>
          <div className="glympse-btnWrapper">
            <CButton className="glympse-cancelBtn" color="light" shape="rounded-pill" variant="ghost" onClick={ handleClose }>Cancel</CButton>
          </div>
          </CModalFooter>
        </FormikProvider>
      </CForm>
    </CModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  saving: state?.userManagement?.updateUser?.saving,
  saved: state?.userManagement?.updateUser?.saved,
});

export default connect(mapStateToProps)(EditTechnicianModal);
